import { Button, Checkbox, Input, message, Select, Space, Spin, Tag } from "antd";
import {
  CheckOutlined,
  CloseOutlined,
  DownloadOutlined,
  EyeOutlined,
  FilterOutlined,
  PlusOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { serverFetch } from "../../server";
import { useViewportBreakpoint } from "../../hooks/useViewportBreakpoint";
import "../company/company.css";
import "../orders/order.css";
import Plugin from "./plugin";
import PluginView from "./plugin-view";
import "./plugins.css";

const Plugins = () => {
  const { pluginId: urlPluginId } = useParams();
  const navigate = useNavigate();
  const user = useSelector(s => s.user);
  const isMobile = useViewportBreakpoint() === "mobile";

  const [programs, setPrograms] = useState([]);
  const [selectedProgId, setSelectedProgId] = useState(null);
  const [plugins, setPlugins] = useState([]);
  const [selectedPlugin, setSelectedPlugin] = useState(null);
  const [loadingPrograms, setLoadingPrograms] = useState(false);
  const [loadingPlugins, setLoadingPlugins] = useState(false);
  const [showDeleted, setShowDeleted] = useState(false);
  const [tags, setTags] = useState([]);
  const [selectedTagIds, setSelectedTagIds] = useState([]);
  const [searchText, setSearchText] = useState("");

  const [filterOpen, setFilterOpen] = useState(false);
  const [draftSelectedProgId, setDraftSelectedProgId] = useState(null);
  const [draftSelectedTagIds, setDraftSelectedTagIds] = useState([]);
  const [draftShowDeleted, setDraftShowDeleted] = useState(false);
  const [filterModalTags, setFilterModalTags] = useState([]);

  useEffect(() => {
    setLoadingPrograms(true);
    const progCompany = process.env.REACT_APP_PROG_COMPANY;
    const url = progCompany ? `dict/prog?progCompany=${progCompany}` : "dict/prog";
    serverFetch(url)
      .then(data => {
        setLoadingPrograms(false);
        setPrograms(Array.isArray(data) ? data : []);
        if (data?.length > 0) {
          const saved = localStorage.getItem("plugins_selectedProgId");
          const exists = saved && data.some(p => p.id === parseInt(saved, 10));
          setSelectedProgId(exists ? parseInt(saved, 10) : data[0].id);
        }
      })
      .catch(e => {
        setLoadingPrograms(false);
        message.error(e?.userMessage || "Ошибка загрузки программ");
      });
  }, []);

  useEffect(() => {
    if (selectedProgId != null) localStorage.setItem("plugins_selectedProgId", String(selectedProgId));
  }, [selectedProgId]);

  useEffect(() => {
    if (!selectedProgId) {
      setTags([]);
      setSelectedTagIds([]);
      return;
    }
    serverFetch("Plugin/tags", { queryParams: { progId: selectedProgId } })
      .then(data => setTags(Array.isArray(data) ? data : []))
      .catch(() => setTags([]));
    setSelectedTagIds([]);
  }, [selectedProgId]);

  useEffect(() => {
    if (!filterOpen || !draftSelectedProgId) {
      setFilterModalTags([]);
      return;
    }
    serverFetch("Plugin/tags", { queryParams: { progId: draftSelectedProgId } })
      .then(data => setFilterModalTags(Array.isArray(data) ? data : []))
      .catch(() => setFilterModalTags([]));
  }, [filterOpen, draftSelectedProgId]);

  const loadPlugins = () => {
    if (!selectedProgId) return;
    setLoadingPlugins(true);
    const queryParams = { progId: selectedProgId };
    if (user?.admin && showDeleted) queryParams.showDeleted = true;
    serverFetch("Plugin/plugins", { queryParams })
      .then(data => {
        setLoadingPlugins(false);
        setPlugins(Array.isArray(data) ? data : []);
      })
      .catch(e => {
        setLoadingPlugins(false);
        message.error(e?.userMessage || "Ошибка загрузки плагинов");
      });
  };

  useEffect(() => {
    if (!selectedProgId) {
      setPlugins([]);
      setSelectedPlugin(null);
      return;
    }
    if (!urlPluginId) setSelectedPlugin(null);
    loadPlugins();
  }, [selectedProgId, showDeleted]);

  const filteredByTags =
    selectedTagIds.length === 0
      ? plugins
      : plugins.filter(pl => pl.tags?.some(t => selectedTagIds.includes(t.id)));

  const searchLower = searchText.trim().toLowerCase();
  const filteredPlugins =
    searchLower === ""
      ? filteredByTags
      : filteredByTags.filter(
          pl =>
            (pl.name && pl.name.toLowerCase().includes(searchLower)) ||
            (pl.description && pl.description.toLowerCase().includes(searchLower))
        );

  useEffect(() => {
    if (!urlPluginId || filteredPlugins.length === 0) return;
    const id = parseInt(urlPluginId, 10);
    if (Number.isNaN(id)) return;
    const found =
      filteredPlugins.find(p => p.id === id) ?? filteredByTags.find(p => p.id === id) ?? plugins.find(p => p.id === id);
    if (found) {
      setSelectedPlugin(found);
    } else {
      navigate("/plugins", { replace: true });
    }
  }, [urlPluginId, plugins]);

  const filterSummary = useMemo(() => {
    const parts = [];
    const prog = programs.find(p => p.id === selectedProgId);
    if (prog) parts.push(prog.name);
    else parts.push("программа не выбрана");
    if (selectedTagIds.length > 0) {
      const names = selectedTagIds.map(id => tags.find(t => t.id === id)?.name).filter(Boolean);
      if (names.length) parts.push(`метки: ${names.join(", ")}`);
      else parts.push(`меток: ${selectedTagIds.length}`);
    }
    if (user?.admin && showDeleted) parts.push("неактивные");
    const q = searchText.trim();
    if (q) parts.push(`поиск: ${q.length > 40 ? `${q.slice(0, 40)}…` : q}`);
    return parts.join(" · ");
  }, [programs, selectedProgId, selectedTagIds, tags, showDeleted, user?.admin, searchText]);

  const openFilterModal = () => {
    setDraftSelectedProgId(selectedProgId);
    setDraftSelectedTagIds(selectedTagIds);
    setDraftShowDeleted(showDeleted);
    setFilterOpen(true);
  };

  const cancelFilter = () => setFilterOpen(false);

  const applyFilter = () => {
    if (!draftSelectedProgId) {
      message.warning("Выберите программу");
      return;
    }
    const progChanged = draftSelectedProgId !== selectedProgId;
    setSelectedProgId(draftSelectedProgId);
    setShowDeleted(draftShowDeleted);
    if (!progChanged) {
      setSelectedTagIds(draftSelectedTagIds);
    }
    setFilterOpen(false);
  };

  const handleSearchKeyDown = e => {
    if (e.key === "Enter") {
      loadPlugins();
    }
  };

  const handleAddPlugin = () => {
    if (!selectedProgId) return;
    const draft = {
      progId: selectedProgId,
      name: "Новый плагин",
      act: true,
      description: "",
      needPayment: false,
      idx: plugins.length,
      ver: 1,
    };
    setSelectedPlugin(draft);
    navigate("/plugins");
  };

  const handlePluginSaved = () => {
    setSelectedPlugin(null);
    navigate("/plugins");
    loadPlugins();
  };

  const handlePluginDeleted = () => {
    setSelectedPlugin(null);
    navigate("/plugins");
    loadPlugins();
  };

  const cardsInner = (
    <>
      {loadingPlugins ? (
        <div className="plugins-cards-loading">
          <Spin />
        </div>
      ) : filteredPlugins.length > 0 ? (
        <div className="plugins-cards">
          {filteredPlugins.map(pl => (
            <div
              key={pl.id}
              className={`plugins-card ${selectedPlugin?.id === pl.id ? "selected" : ""}`}
              onClick={() => {
                setSelectedPlugin(pl);
                navigate(`/plugins/${pl.id}`);
              }}
            >
              <div className="plugins-card-body">
                <div className={`plugins-card-name ${pl.act === false ? "plugins-card-name-inactive" : ""}`}>
                  {pl.name || `#${pl.id}`}
                </div>
                <div className="plugins-card-desc">{pl.description || ""}</div>
                {pl.tags?.length > 0 && (
                  <div className="plugins-card-tags">
                    {pl.tags.map(t => (
                      <Tag key={t.id} color={t.color || undefined}>
                        {t.name}
                      </Tag>
                    ))}
                  </div>
                )}
                {(pl.updatedOn || pl.needPayment === false || pl.qtyView != null || pl.qtyDownload != null) && (
                  <div className="plugins-card-footer">
                    {pl.updatedOn && (
                      <span className="plugins-card-updated">{new Date(pl.updatedOn).toLocaleString()}</span>
                    )}
                    {pl.needPayment === false && (
                      <Tag variant="outlined" color="gold">
                        подписка
                      </Tag>
                    )}
                    <span className="plugins-card-stats">
                      <span>
                        {pl.qtyView ?? 0} <EyeOutlined />
                      </span>
                      <span>
                        {pl.qtyDownload ?? 0} <DownloadOutlined />
                      </span>
                    </span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="plugins-empty-state">
          <p>
            {plugins.length === 0
              ? "Нет плагинов по программе"
              : filteredByTags.length === 0
                ? "Нет плагинов по выбранным меткам"
                : "Нет плагинов по запросу"}
          </p>
        </div>
      )}
    </>
  );

  const closePluginModal = () => {
    setSelectedPlugin(null);
    navigate("/plugins");
  };

  const pluginModalInner =
    user?.admin ? (
      <Plugin
        plugin={selectedPlugin}
        user={user}
        onSaved={handlePluginSaved}
        onCancel={closePluginModal}
        onDeleted={handlePluginDeleted}
      />
    ) : (
      <PluginView plugin={selectedPlugin} user={user} onCancel={closePluginModal} />
    );

  const pluginModal =
    selectedPlugin &&
    (isMobile ? (
      <div
        className="misoft-modal-overlay company-base-overlay--mobile plugins-mobile-plugin-modal"
        onClick={e => e.target === e.currentTarget && closePluginModal()}
      >
        <div
          className="misoft-modal-content company-base-sheet--mobile plugins-mobile-plugin-panel"
          onClick={e => e.stopPropagation()}
        >
          {pluginModalInner}
        </div>
      </div>
    ) : (
      <>
        <div className="plugins-modal-overlay" onClick={closePluginModal} aria-hidden />
        <div className="plugins-modal-panel">{pluginModalInner}</div>
      </>
    ));

  return (
    <>
      {isMobile ? (
        <div className="page orders-page--mobile plugins-page--mobile">
          <div className="plugins-mobile-body">
            <div className="orders-mobile-toolbar">
              <div className="company-backups-mobile-search-row">
                <Input
                  placeholder="Поиск по названию и описанию"
                  value={searchText}
                  onChange={e => setSearchText(e.target.value)}
                  onKeyDown={handleSearchKeyDown}
                  allowClear
                  className="company-backups-mobile-search-input"
                />
              </div>
              <div className="company-backups-mobile-filter-row">
                <span className="company-backups-mobile-filter-summary" title={filterSummary}>
                  {filterSummary}
                </span>
                <Button
                  type="default"
                  icon={<FilterOutlined />}
                  onClick={openFilterModal}
                  aria-label="Фильтр"
                  className="company-backups-mobile-filter-icon-btn"
                />
                <Button
                  type="default"
                  icon={<ReloadOutlined />}
                  onClick={loadPlugins}
                  loading={loadingPlugins}
                  disabled={!selectedProgId}
                  aria-label="Обновить"
                  className="company-backups-mobile-refresh-btn"
                />
                {user?.admin ? (
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={handleAddPlugin}
                    disabled={!selectedProgId}
                    aria-label="Добавить"
                    className="company-backups-mobile-filter-icon-btn"
                  />
                ) : null}
              </div>
            </div>
            <div className="orders-mobile-scroll plugins-mobile-scroll">
              <div className="plugins-cards-wrap plugins-cards-wrap--mobile">{cardsInner}</div>
            </div>
          </div>
          {filterOpen && (
            <div
              className="misoft-modal-overlay company-backups-filter-overlay--top"
              onClick={e => e.target === e.currentTarget && cancelFilter()}
            >
              <div
                className="misoft-modal-content company-backups-filter-sheet--top"
                onClick={e => e.stopPropagation()}
              >
                <div className="company-base-sheet-header">
                  <span className="company-base-sheet-title">Фильтр</span>
                  <Space size="small" className="company-base-sheet-actions">
                    <Button
                      type="primary"
                      icon={<CheckOutlined />}
                      onClick={applyFilter}
                      aria-label="Применить"
                      className="company-base-header-icon-btn"
                    />
                    <Button
                      type="default"
                      icon={<CloseOutlined />}
                      onClick={cancelFilter}
                      aria-label="Отмена"
                      className="company-base-header-icon-btn"
                    />
                  </Space>
                </div>
                <div className="company-base-sheet-body company-backups-filter-sheet-body">
                  <div className="company-backups-filter-field">
                    <span className="company-backups-filter-label">Программа</span>
                    <Select
                      placeholder="Выберите программу"
                      value={draftSelectedProgId}
                      onChange={v => {
                        setDraftSelectedProgId(v);
                        setDraftSelectedTagIds([]);
                      }}
                      loading={loadingPrograms}
                      options={programs.map(p => ({ label: p.name, value: p.id }))}
                      style={{ width: "100%" }}
                    />
                  </div>
                  {filterModalTags.length > 0 && (
                    <div className="company-backups-filter-field">
                      <span className="company-backups-filter-label">Метки</span>
                      <Select
                        mode="multiple"
                        placeholder="Метки (фильтр)"
                        value={draftSelectedTagIds}
                        onChange={setDraftSelectedTagIds}
                        options={filterModalTags.map(t => ({ label: t.name, value: t.id }))}
                        allowClear
                        style={{ width: "100%" }}
                      />
                    </div>
                  )}
                  {user?.admin ? (
                    <div className="company-backups-filter-field company-backups-filter-switch-row">
                      <span>Показывать неактивные</span>
                      <Checkbox checked={draftShowDeleted} onChange={e => setDraftShowDeleted(e.target.checked)} />
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          )}
          {pluginModal}
        </div>
      ) : (
        <div className="plugins-container">
          <div className="plugins-filters-row">
            <Select
              className="plugins-program-select"
              placeholder="Выберите программу"
              value={selectedProgId}
              onChange={setSelectedProgId}
              loading={loadingPrograms}
              options={programs.map(p => ({ label: p.name, value: p.id }))}
            />
            {tags.length > 0 && (
              <Select
                mode="multiple"
                placeholder="Метки (фильтр)"
                value={selectedTagIds}
                onChange={setSelectedTagIds}
                options={tags.map(t => ({ label: t.name, value: t.id }))}
                allowClear
                className="plugins-tags-select"
              />
            )}
            <Input
              placeholder="Поиск по названию и описанию"
              value={searchText}
              onChange={e => setSearchText(e.target.value)}
              allowClear
              className="plugins-search-input"
            />
            {user?.admin && (
              <Checkbox checked={showDeleted} onChange={e => setShowDeleted(e.target.checked)}>
                Показывать неактивные
              </Checkbox>
            )}
            {user?.admin && (
              <Button type="primary" onClick={handleAddPlugin} disabled={!selectedProgId} className="plugins-add-button">
                Добавить
              </Button>
            )}
            <Button onClick={loadPlugins} disabled={!selectedProgId} loading={loadingPlugins}>
              Обновить
            </Button>
          </div>

          <div className="plugins-cards-wrap">{cardsInner}</div>
          {pluginModal}
        </div>
      )}
    </>
  );
};

export default Plugins;
